import type {
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyElementJson,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type {
  SelectBooleanOption,
  SelectCheckboxOption,
  SelectDropdownOption,
  SelectImagepickerOption,
  SelectRadiogroupOption,
  SelectRatingOption,
} from '../../utils'
import { SurveyElementFormHandlerBoolean } from './survey-element-form-handler-boolean'
import { SurveyElementFormHandlerDate } from './survey-element-form-handler-date'
import { SurveyElementFormHandlerFile } from './survey-element-form-handler-file'
import { SurveyElementFormHandlerImagepicker } from './survey-element-form-handler-imagepicker'
import { SurveyElementFormHandlerNumber } from './survey-element-form-handler-number'
import { SurveyElementFormHandlerPanel } from './survey-element-form-handler-panel'
import { SurveyElementFormHandlerRadiogroup } from './survey-element-form-handler-radiogroup'
import { SurveyElementFormHandlerRating } from './survey-element-form-handler-rating'
import { SurveyElementFormHandlerSurveyModel } from './survey-element-form-handler-survey-model'
import { SurveyElementFormHandlerText } from './survey-element-form-handler-text'

type SelectOption
  = | SelectBooleanOption
    | SelectCheckboxOption
    | SelectDropdownOption
    | SelectImagepickerOption
    | SelectRadiogroupOption
    | SelectRatingOption

export interface SurveyElementFormHandler {
  elementJson: SurveyElementJson
  surveyModel: SurveyModel | null
  // 最終的にapiに送信する回答データ
  data: SurveyAnswerRecord
  // コメント以外の選択や、入力したデータ
  getValue: () => SurveyAnswerValue
  // コメント入力の時データ
  getComment: () => string
  setElementResultRecordValue: (value: SurveyAnswerValue) => void
  // コメント入力のためのfunction
  setElementResultRecordComment: (comment: string) => void
  validate: () => {
    isValid: boolean
    errorMessage?: string
  }
  setUploadingState?: (isUploading: boolean) => void
  getUploadingState?: () => boolean
  // コメント以外の選択や、入力したデータ for other option
  // 以下 otherの対応 function
  // その他（説明)の時にvalueを保存するため。
  // setElementResultRecordValueを使わないのはselect(radio, checkbox) optionでは valueが'other'になっているが、回答jsonでは'other'ではなく、入力のテキストが保存されるので
  // べつのfunctionを作る。setElementResultRecordValueには select option時のvalueをセット。実際に説明入力はこのfunctionを使う。
  setElementResultRecordOtherValue?: (otherText: string) => void
  // 選択肢の設問の場合のoptionを取得する
  getSelectOptions?: () => SelectOption[]
  // 基本は getValue()と同じだが、radiogroupのように'other'を使う場合には getValue()では、入力テキストを取得される。
  // UIの制御にはoption value,つまり 'other' を使う必要があるので、getSelectOptionValueを用意する。
  getSelectOptionValue?: () => SurveyAnswerValue
}

/**
 * SurveyModelをproxyするためのクラス
 */
export class SurveyElementForm implements SurveyElementFormHandler {
  #handler: SurveyElementFormHandlerSurveyModel
    | SurveyElementFormHandlerRadiogroup
    | SurveyElementFormHandlerBoolean
    | SurveyElementFormHandlerImagepicker
    | SurveyElementFormHandlerFile
    | SurveyElementFormHandlerText
    | SurveyElementFormHandlerNumber
    | SurveyElementFormHandlerDate
    | SurveyElementFormHandlerPanel
    | SurveyElementFormHandlerRating

  constructor(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
    surveyModel: SurveyModel | null,
  ) {
    this.#handler = this._buildHandler(elementJson, elementResultRecord, surveyModel)
  }

  _buildHandler(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
    surveyModel: SurveyModel | null,
  ) {
    switch (elementJson.type) {
    case 'radiogroup':
      return new SurveyElementFormHandlerRadiogroup(
        elementJson,
        elementResultRecord,
      )
    case 'boolean':
      return new SurveyElementFormHandlerBoolean(
        elementJson,
        elementResultRecord,
      )
    case 'file':
      return new SurveyElementFormHandlerFile(
        elementJson,
        elementResultRecord,
      )
    case 'imagepicker':
      return new SurveyElementFormHandlerImagepicker(
        elementJson,
        elementResultRecord,
      )
    case 'text':
      return new SurveyElementFormHandlerText(
        elementJson,
        elementResultRecord,
      )
    case 'comment':
      return new SurveyElementFormHandlerText(
        elementJson,
        elementResultRecord,
      )
    case 'number':
      return new SurveyElementFormHandlerNumber(
        elementJson,
        elementResultRecord,
      )
    case 'date':
      return new SurveyElementFormHandlerDate(
        elementJson,
        elementResultRecord,
      )
    case 'panel':
      return new SurveyElementFormHandlerPanel(
        elementJson,
        elementResultRecord,
      )
    case 'rating':
      return new SurveyElementFormHandlerRating(
        elementJson,
        elementResultRecord || undefined,
        surveyModel || undefined,
      )
    default:
      return new SurveyElementFormHandlerSurveyModel(
        elementJson,
        surveyModel,
      )
    }
  }

  get elementJson() {
    return this.#handler.elementJson
  }

  get surveyModel() {
    return this.#handler.surveyModel
  }

  /**
   * custom elementの場合は、setElementResultRecordによってelementResultRecordにデータがセットされるので
   * elementResultRecordから取得
   * custom elementではない場合には、surveyModel.dataから取得
   * NOTE: SurveyElementFileは、custom elementだけど、内部でSurveyModelを使っているので surveyModel.dataから取得
   */
  get data(): SurveyAnswerRecord {
    return this.#handler.data
  }

  /**
   * 現在のelementResultRecordから、値の部分だけを取得する
   */
  getValue() {
    return this.#handler.getValue()
  }

  getComment(): string {
    return this.#handler.getComment()
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    this.#handler.setElementResultRecordValue(value)
  }

  setElementResultRecordComment(comment: string) {
    this.#handler.setElementResultRecordComment(comment)
  }

  /**
   * formSchemaがある場合は、zodでvalidateする (@see createSurveyElementSchema)
   * formSchemaがない場合は、surveyModelでvalidateする
   */
  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    return this.#handler.validate()
  }

  setUploadingState(isUploading: boolean): void {
    if ('setUploadingState' in this.#handler) {
      this.#handler.setUploadingState(isUploading)
    }
  }

  getUploadingState(): boolean {
    return 'getUploadingState' in this.#handler ? this.#handler.getUploadingState() : false
  }

  setElementResultRecordOtherValue(otherText: string) {
    if ('setElementResultRecordOtherValue' in this.#handler) {
      this.#handler.setElementResultRecordOtherValue(otherText)
    }
  }

  getSelectOptions(): SelectOption[] {
    if ('getSelectOptions' in this.#handler) {
      return this.#handler.getSelectOptions()
    }
    return []
  }

  getSelectOptionValue(): SurveyAnswerValue {
    if ('getSelectOptionValue' in this.#handler) {
      return this.#handler.getSelectOptionValue()
    }
    return ''
  }
}
